import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HandshakeIcon from "@mui/icons-material/Handshake";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { API_BASE_URL } from "../../../config/api";

// ── Zod validation schema ────────────────────────────────
const partnerSchema = z.object({
  name: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name is too long"),
  phone: z
    .string()
    .regex(
      /^[6-9]\d{9}$/,
      "Enter a valid 10-digit Indian phone number"
    ),
  email: z.string().email("Enter a valid email address"),
  city: z
    .string()
    .min(2, "City / State is required")
    .max(100, "City / State is too long"),
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function PartnerModal({ open, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(partnerSchema),
    defaultValues: { name: "", phone: "", email: "", city: "" },
  });

  const onSubmit = async (data) => {
    setSubmitting(true);
    setApiError("");
    try {
      await axios.post(`${API_BASE_URL}/api/partners`, data);
      setSuccess(true);
      reset();
    } catch (err) {
      setApiError(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSuccess(false);
      setApiError("");
      reset();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)",
          color: "#fff",
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header ────────────────────────────── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(90deg, #5c4dff 0%, #8b5cf6 100%)",
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HandshakeIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight={700}>
            Become a Partner
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: "#fff" }} disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ── Success State ─────────────────────── */}
      {success ? (
        <DialogContent sx={{ textAlign: "center", py: 6 }}>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 72, color: "#4caf50", mb: 2 }}
          />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Application Submitted!
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.8)", maxWidth: 400, mx: "auto" }}
          >
            Thank you! Your partner application has been submitted. We will
            contact you soon.
          </Typography>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              mt: 4,
              borderRadius: 2,
              px: 4,
              py: 1,
              background: "linear-gradient(90deg, #5c4dff, #8b5cf6)",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Done
          </Button>
        </DialogContent>
      ) : (
        /* ── Form State ──────────────────────── */
        <>
          <DialogContent sx={{ pt: 3, pb: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", mb: 3 }}
            >
              Fill in your details below and our team will reach out to you.
            </Typography>

            {apiError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {apiError}
              </Alert>
            )}

            <Box
              component="form"
              id="partner-form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
            >
              <TextField
                label="Full Name"
                fullWidth
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
                InputProps={{ style: { color: "#fff" } }}
                InputLabelProps={{
                  style: { color: "rgba(255,255,255,0.7)" },
                }}
                sx={textFieldSx}
              />

              <TextField
                label="Phone Number"
                fullWidth
                placeholder="e.g. 9876543210"
                {...register("phone")}
                error={!!errors.phone}
                helperText={errors.phone?.message}
                InputProps={{ style: { color: "#fff" } }}
                InputLabelProps={{
                  style: { color: "rgba(255,255,255,0.7)" },
                }}
                sx={textFieldSx}
              />

              <TextField
                label="Email Address"
                fullWidth
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email?.message}
                InputProps={{ style: { color: "#fff" } }}
                InputLabelProps={{
                  style: { color: "rgba(255,255,255,0.7)" },
                }}
                sx={textFieldSx}
              />

              <TextField
                label="City / State"
                fullWidth
                placeholder="e.g. Mumbai, Maharashtra"
                {...register("city")}
                error={!!errors.city}
                helperText={errors.city?.message}
                InputProps={{ style: { color: "#fff" } }}
                InputLabelProps={{
                  style: { color: "rgba(255,255,255,0.7)" },
                }}
                sx={textFieldSx}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleClose}
              sx={{
                color: "rgba(255,255,255,0.7)",
                textTransform: "none",
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="partner-form"
              variant="contained"
              disabled={submitting}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1,
                background: "linear-gradient(90deg, #5c4dff, #8b5cf6)",
                fontWeight: 600,
                textTransform: "none",
                minWidth: 140,
                "&:hover": {
                  background: "linear-gradient(90deg, #4b3de6, #7c4ddb)",
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Apply Now"
              )}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

/* ── Shared text field styling ─────────────────────────── */
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.25)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#8b5cf6",
    },
  },
  "& .MuiFormHelperText-root": {
    color: "#f87171",
  },
};
